// apps/web/src/pages/HowItWorksPage.tsx
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  ArrowRight,
  UserCheck,
  PackagePlus,
  Truck,
  Briefcase,
  IdCard,
  Send,
  Navigation,
  CheckCircle,
  AlertTriangle,
  Clock,
  ShieldCheck,
  CheckCircle2,
  ChevronRight,
  Layers,
  PhoneCall,
  Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import TextReveal from '@/components/ui/TextReveal';
import GradientBackground from '@/components/ui/GradientBackground';
import { TUTORIAL_MODULES, TutorialModule } from '@/data/howToTutorials';
import AdaptiveGuideVideoPlayer from '@/components/tutorials/AdaptiveGuideVideoPlayer';

// Role definitions mapping to specific module IDs
interface RoleSection {
  id: string;
  label: string;
  badge: string;
  icon: any;
  color: string;
  headline: string;
  subheadline: string;
  ctaText: string;
  ctaLink: string;
  moduleIds: string[];
}

const ROLES: RoleSection[] = [
  {
    id: 'client',
    label: 'Cargo Owners',
    badge: 'Clients & Shippers',
    icon: PackagePlus,
    color: 'from-blue-600 to-indigo-600',
    headline: 'Ship Cargo Securely',
    subheadline: 'Post loads in seconds, receive fair AI pricing, fund escrow, and track your goods in real-time.',
    ctaText: 'Post a Job Now',
    ctaLink: '/register?role=CLIENT',
    moduleIds: ['registration-onboarding', 'creating-a-job', 'tracking-transporters']
  },
  {
    id: 'transporter',
    label: 'Transporters',
    badge: 'Owner-Operators',
    icon: Truck,
    color: 'from-emerald-600 to-teal-600',
    headline: 'Keep Your Wheels Moving',
    subheadline: 'Browse verified high-value loads, submit instant bids, upload COF documents, and receive guaranteed payouts.',
    ctaText: 'Start Hauling',
    ctaLink: '/register?role=TRANSPORTER',
    moduleIds: ['registration-onboarding', 'adding-a-vehicle', 'booking-a-job', 'job-lifecycle-stages']
  },
  {
    id: 'fleet-manager',
    label: 'Fleet Managers',
    badge: 'Logistics Companies',
    icon: Layers,
    color: 'from-cyan-600 to-blue-600',
    headline: 'Scale Fleet Operations',
    subheadline: 'Register multiple commercial vehicles, invite drivers, dispatch secured loads, and centralize company earnings.',
    ctaText: 'Register Fleet',
    ctaLink: '/register?role=FLEET_MANAGER',
    moduleIds: ['registration-onboarding', 'adding-a-vehicle', 'booking-a-job', 'dispatching-loads']
  },
  {
    id: 'fleet-driver',
    label: 'Fleet Drivers',
    badge: 'Hired Drivers',
    icon: IdCard,
    color: 'from-indigo-600 to-purple-600',
    headline: 'Hassle-Free Driving',
    subheadline: 'Join established company fleets with an invite code, accept trip dispatches, and log digital proof of delivery.',
    ctaText: 'Join as Driver',
    ctaLink: '/register/driver',
    moduleIds: ['driver-registration', 'job-lifecycle-stages']
  },
  {
    id: 'emergency',
    label: 'Emergency & Safety',
    badge: 'Critical Protocol',
    icon: AlertTriangle,
    color: 'from-rose-600 to-red-600',
    headline: 'Roadside SOS & Protocol',
    subheadline: 'Mechanical breakdowns, road incidents, cargo safety checks, and emergency transshipment coordination.',
    ctaText: 'View Safety Center',
    ctaLink: '/help',
    moduleIds: ['emergency-actions-sos', 'tracking-transporters']
  }
];

// Icon mapping helper
const getModuleIcon = (iconName: string) => {
  switch (iconName) {
    case 'UserCheck':
      return UserCheck;
    case 'PackagePlus':
      return PackagePlus;
    case 'Truck':
      return Truck;
    case 'Briefcase':
      return Briefcase;
    case 'IdCard':
      return IdCard;
    case 'Send':
      return Send;
    case 'Navigation':
      return Navigation;
    case 'CheckCircle':
      return CheckCircle;
    case 'AlertTriangle':
      return AlertTriangle;
    default:
      return Sparkles;
  }
};

const HowItWorksPage: React.FC = () => {
  const [activeRoleId, setActiveRoleId] = useState<string>('client');
  const activeRole = ROLES.find((r) => r.id === activeRoleId) || ROLES[0];

  // Get modules for active role
  const roleModules = activeRole.moduleIds
    .map((id) => TUTORIAL_MODULES.find((m) => m.id === id))
    .filter(Boolean) as TutorialModule[];

  const [activeModuleId, setActiveModuleId] = useState<string>(roleModules[0]?.id || TUTORIAL_MODULES[0].id);
  const [activeTimestamp, setActiveTimestamp] = useState<string | undefined>(undefined);

  const videoPlayerSectionRef = useRef<HTMLDivElement>(null);

  // When role changes, set active module to first in that role
  const handleRoleChange = (roleId: string) => {
    setActiveRoleId(roleId);
    const targetRole = ROLES.find((r) => r.id === roleId);
    if (targetRole && targetRole.moduleIds.length > 0) {
      setActiveModuleId(targetRole.moduleIds[0]);
      setActiveTimestamp(undefined);
    }
  };

  const activeModule = TUTORIAL_MODULES.find((m) => m.id === activeModuleId) || roleModules[0] || TUTORIAL_MODULES[0];
  const ActiveModuleIcon = getModuleIcon(activeModule.iconName);

  const handleStepJump = (timestamp?: string) => {
    if (timestamp) {
      setActiveTimestamp(timestamp);
      if (videoPlayerSectionRef.current) {
        videoPlayerSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  };

  return (
    <div className="bg-background text-foreground min-h-screen relative font-sans selection:bg-primary/30">
      <GradientBackground />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-4 z-10">
        <div className="container mx-auto max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-md mb-8 shadow-sm"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-primary text-xs md:text-sm font-bold tracking-wider uppercase">
              Official Platform Guide & Video Walkthroughs
            </span>
          </motion.div>

          <div className="flex flex-col items-center mb-6">
            <TextReveal className="text-5xl sm:text-7xl md:text-8xl font-black tracking-tight text-foreground leading-[0.95]">
              HOW IT WORKS
            </TextReveal>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed font-light mb-12"
          >
            Everything you need to know about transporting, shipping, and managing fleets with K&C Logistics.
            Videos automatically adapt to your <span className="font-semibold text-foreground">Light</span> and{' '}
            <span className="font-semibold text-foreground">Dark</span> mode preferences.
          </motion.p>

          {/* Master Role Switcher Tabs */}
          <div className="flex justify-center mb-10">
            <div className="p-1.5 rounded-full bg-foreground/[0.06] border border-foreground/10 backdrop-blur-xl shadow-2xl flex flex-wrap items-center justify-center gap-1">
              {ROLES.map((role) => {
                const RoleIcon = role.icon;
                const isSelected = role.id === activeRoleId;

                return (
                  <button
                    key={role.id}
                    onClick={() => handleRoleChange(role.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold transition-all ${
                      isSelected
                        ? 'bg-primary text-black shadow-lg scale-105'
                        : 'text-muted-foreground hover:text-foreground hover:bg-foreground/5'
                    }`}
                  >
                    <RoleIcon className="w-4 h-4" />
                    <span>{role.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Main Theater Stage Section */}
      <section ref={videoPlayerSectionRef} className="container mx-auto px-4 max-w-6xl pb-24 relative z-10">
        {/* Role Headline Banner */}
        <div className="p-8 sm:p-10 rounded-[2.5rem] bg-foreground/[0.03] border border-foreground/10 backdrop-blur-xl mb-10 shadow-sm relative overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="text-xs px-3 py-0.5 border-primary/30 text-primary font-bold">
                  {activeRole.badge}
                </Badge>
                <span className="text-xs text-muted-foreground font-mono">
                  {roleModules.length} Dedicated Walkthroughs
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-foreground">
                {activeRole.headline}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mt-1 leading-relaxed">
                {activeRole.subheadline}
              </p>
            </div>

            <Button asChild size="lg" className="rounded-full px-8 h-12 text-sm font-bold shadow-lg shadow-primary/20 shrink-0">
              <Link to={activeRole.ctaLink}>
                {activeRole.ctaText} <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>

          {/* Module Selector Pills within Role */}
          <div className="flex items-center gap-2 overflow-x-auto pt-8 mt-6 border-t border-foreground/10 no-scrollbar">
            {roleModules.map((mod, idx) => {
              const Icon = getModuleIcon(mod.iconName);
              const isSelected = mod.id === activeModule.id;

              return (
                <button
                  key={mod.id}
                  onClick={() => {
                    setActiveModuleId(mod.id);
                    setActiveTimestamp(undefined);
                  }}
                  className={`flex items-center gap-2.5 px-4 py-2 rounded-2xl text-xs sm:text-sm font-bold whitespace-nowrap transition-all border ${
                    isSelected
                      ? 'bg-foreground text-background border-foreground shadow-md'
                      : 'bg-foreground/[0.03] hover:bg-foreground/[0.08] border-foreground/10 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full text-[11px] font-mono flex items-center justify-center font-bold ${
                    isSelected ? 'bg-primary text-black' : 'bg-foreground/10 text-foreground'
                  }`}>
                    {idx + 1}
                  </span>
                  <Icon className="w-4 h-4" />
                  <span>{mod.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Video Cinema Showcase */}
        <div className="mb-12">
          <AdaptiveGuideVideoPlayer
            module={activeModule}
            activeTimestamp={activeTimestamp}
            onStepClick={handleStepJump}
          />
        </div>

        {/* Interactive Step-by-Step Roadmap (Cards with Timeline) */}
        <div className="space-y-6">
          <div className="flex items-center justify-between px-2">
            <div>
              <h3 className="text-2xl font-black tracking-tight flex items-center gap-2.5">
                <span>Step-by-Step Execution Plan</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-mono font-bold">
                  {activeModule.steps.length} Steps
                </span>
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                Click any step timestamp to jump the video directly to that instruction.
              </p>
            </div>

            <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
              <Clock className="w-3.5 h-3.5 text-primary" />
              <span>Est. time: {activeModule.duration}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeModule.steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => handleStepJump(step.timestamp)}
                className="group p-6 rounded-3xl bg-foreground/[0.03] hover:bg-foreground/[0.06] border border-foreground/10 hover:border-primary/40 transition-all cursor-pointer relative overflow-hidden flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-8 h-8 rounded-xl bg-primary/10 text-primary font-mono font-bold text-sm flex items-center justify-center">
                      0{idx + 1}
                    </span>

                    {step.timestamp && (
                      <span className="inline-flex items-center gap-1 text-xs font-mono font-bold text-primary px-2.5 py-1 rounded-full bg-primary/10 group-hover:bg-primary group-hover:text-black transition-colors">
                        <Play className="w-2.5 h-2.5 fill-current" />
                        {step.timestamp}
                      </span>
                    )}
                  </div>

                  <h4 className="text-base font-bold text-foreground group-hover:text-primary transition-colors mb-2">
                    {step.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-foreground/5 flex items-center text-xs text-primary font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>Seek video to this step</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pro Tips / Safety Requirements Card */}
          <div className="p-6 sm:p-8 rounded-3xl bg-primary/[0.04] border border-primary/20 backdrop-blur-md">
            <h4 className="text-sm font-bold uppercase tracking-wider text-primary mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <span>Platform Safeguards & Verified Best Practices</span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {activeModule.keyTakeaways.map((tip, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3.5 rounded-2xl bg-background/50 border border-foreground/5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span className="text-xs text-muted-foreground leading-relaxed font-medium">
                    {tip}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Role Navigation Cards Grid (Explore all 5 personas) */}
      <section className="container mx-auto px-4 max-w-6xl pb-24 relative z-10">
        <div className="text-center mb-10">
          <h3 className="text-2xl sm:text-3xl font-black tracking-tight mb-2">
            Explore Every Angle of K&C Logistics
          </h3>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Switch between personas to view tutorials customized for your business model.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ROLES.map((role) => {
            const RoleIcon = role.icon;
            const isCurrent = role.id === activeRoleId;

            return (
              <div
                key={role.id}
                onClick={() => {
                  handleRoleChange(role.id);
                  if (videoPlayerSectionRef.current) {
                    videoPlayerSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className={`p-6 rounded-3xl border transition-all cursor-pointer relative overflow-hidden group ${
                  isCurrent
                    ? 'bg-primary/10 border-primary/50 shadow-lg'
                    : 'bg-foreground/[0.03] hover:bg-foreground/[0.07] border-foreground/10'
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-colors ${
                    isCurrent ? 'bg-primary text-black' : 'bg-foreground/10 text-foreground group-hover:bg-primary/20'
                  }`}>
                    <RoleIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-base text-foreground group-hover:text-primary transition-colors">
                      {role.label}
                    </h4>
                    <span className="text-xs text-muted-foreground">{role.moduleIds.length} video guides</span>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                  {role.subheadline}
                </p>

                <div className="flex items-center text-xs font-bold text-primary">
                  <span>{isCurrent ? 'Currently Viewing' : 'Switch to this guide'}</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Grand Bottom Conversion Banner */}
      <section className="container mx-auto px-4 max-w-5xl pb-28 relative z-10">
        <div className="p-10 sm:p-14 rounded-[2.5rem] bg-gradient-to-br from-primary/20 via-primary/5 to-transparent border border-primary/30 text-center relative overflow-hidden shadow-2xl backdrop-blur-xl">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl -z-10" />

          <h3 className="text-3xl sm:text-5xl font-black tracking-tight mb-4 text-foreground">
            Ready to Transport with Confidence?
          </h3>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto mb-8 leading-relaxed">
            Join hundreds of cargo owners, verified transporters, and fleet operators who move goods across Malawi and the SADC region.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="rounded-full px-8 h-12 text-sm font-bold shadow-lg shadow-primary/25">
              <Link to="/register?role=CLIENT">
                Ship Cargo <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-full px-8 h-12 text-sm font-bold border-foreground/20">
              <Link to="/register?role=TRANSPORTER">Join as Transporter</Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="rounded-full px-8 h-12 text-sm font-bold">
              <Link to="/register?role=FLEET_MANAGER">Fleet Operators</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;